import type { CompanyProfile } from '@/lib/companyProfile';

interface Props {
  profile: CompanyProfile;
  customerName: string;
  customerIce: string;
  taxRate: number;
  isQuote: boolean;
}

// Contrôle de conformité affiché dans l'éditeur de document.
// S'appuie sur deux règles concrètes :
//  • Art. 145 : mentions obligatoires (ICE, IF) + identification de l'acheteur.
//  • Paiements fournisseurs : plafond de 5 000 DH/jour/fournisseur (espèces).
export default function InvoiceComplianceBlock({
  profile,
  customerName,
  customerIce,
  taxRate,
  isQuote,
}: Props) {
  const checks = [
    {
      ok: Boolean(profile.company_name.trim()),
      label: 'Raison sociale de l’émetteur',
      hint: 'Bloc 1 des paramètres de facturation',
    },
    {
      ok: Boolean(profile.ice.trim()),
      label: 'ICE de l’émetteur (15 chiffres)',
      hint: 'Obligatoire — Art. 145',
    },
    {
      ok: Boolean(profile.if_number.trim()),
      label: 'Identifiant Fiscal (IF)',
      hint: 'Obligatoire — Art. 145',
    },
    {
      ok: Boolean(profile.rc_number.trim()),
      label: 'Registre de Commerce (RC)',
      hint: 'Recommandé sur toute facture',
    },
    {
      ok: Boolean(customerName.trim()),
      label: 'Identification de l’acheteur',
      hint: 'Nom ou raison sociale',
    },
    {
      ok: Boolean(customerIce.trim()),
      label: 'ICE du client (BtoB)',
      hint: 'Obligatoire entre professionnels',
    },
    {
      ok: taxRate === 0 || profile.vat_subject,
      label: 'TVA cohérente avec votre régime',
      hint: profile.vat_subject
        ? `Assujetti — taux ${taxRate} %`
        : 'Non assujetti : la TVA doit rester à 0 %',
    },
  ];

  const blocking = checks.filter((c) => !c.ok && (c.label.includes('émetteur') || c.label.includes('(IF)') || c.label.includes('acheteur') || c.label.startsWith('TVA')));
  const okCount = checks.filter((c) => c.ok).length;

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-shield-check-line text-primary-500"></i>
          Conformité du document
        </h3>
        <span
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
            blocking.length === 0 ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-800'
          }`}
        >
          {okCount}/{checks.length} mentions
        </span>
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {checks.map((c) => (
          <li key={c.label} className="flex items-start gap-2 text-xs">
            <i
              className={`mt-0.5 flex-shrink-0 ${
                c.ok ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-error-warning-line text-accent-600'
              }`}
            />
            <span className="min-w-0">
              <span className={c.ok ? 'text-foreground-700' : 'text-foreground-900 font-medium'}>{c.label}</span>
              <span className="text-foreground-400"> · {c.hint}</span>
            </span>
          </li>
        ))}
      </ul>

      {!isQuote && blocking.length > 0 && (
        <p className="mt-3 text-[11px] text-accent-800 bg-accent-50 rounded-md px-2.5 py-2 flex items-start gap-1.5">
          <i className="ri-alert-line mt-0.5 flex-shrink-0"></i>
          L’enregistrement de la facture est bloqué tant que l’ICE, l’IF et l’identité du client ne sont pas renseignés.
        </p>
      )}
      {isQuote && (
        <p className="mt-3 text-[11px] text-foreground-500 flex items-start gap-1.5">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          Un devis n’a pas de valeur fiscale : il devient une facture conforme une fois accepté puis facturé.
        </p>
      )}
    </section>
  );
}